import { useState, useEffect } from "react";

interface TypingAnimationProps {
  slogans: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseDuration?: number;
}

// Helper function to find the common prefix between two strings
const getCommonPrefix = (str1: string, str2: string): string => {
  if (!str1 || !str2) return "";
  let i = 0;
  while (i < str1.length && i < str2.length && str1[i] === str2[i]) {
    i++;
  }
  return str1.substring(0, i);
};

const TypingAnimation = ({ 
  slogans, 
  typingSpeed = 100, 
  deletingSpeed = 50, 
  pauseDuration = 2000 
}: TypingAnimationProps) => {
  const [currentSloganIndex, setCurrentSloganIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!slogans || slogans.length === 0) return;
    
    const currentSlogan = slogans[currentSloganIndex];
    const nextSlogan = slogans[(currentSloganIndex + 1) % slogans.length];
    
    if (!currentSlogan || !nextSlogan) return;
    
    const commonPrefix = getCommonPrefix(currentSlogan, nextSlogan);

    if (isPaused) {
      const pauseTimeout = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseDuration);
      return () => clearTimeout(pauseTimeout);
    }

    if (!isDeleting && currentText === currentSlogan) {
      setIsPaused(true);
      return;
    }

    if (isDeleting && currentText === commonPrefix) {
      setIsDeleting(false);
      setCurrentSloganIndex((prevIndex) => (prevIndex + 1) % slogans.length);
      return;
    }

    const timeout = setTimeout(
      () => {
        setCurrentText((prev) => {
          if (isDeleting) {
            return currentSlogan.substring(0, prev.length - 1);
          } else {
            return currentSlogan.substring(0, prev.length + 1);
          }
        });
      },
      isDeleting ? deletingSpeed : typingSpeed
    );

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, isPaused, currentSloganIndex, slogans, typingSpeed, deletingSpeed, pauseDuration]);

  return (
    <span className="inline-block min-h-[1.2em]">
      {currentText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

export default TypingAnimation;
